import React, { useState, useEffect, useMemo, memo } from 'react'
import { Card, Select, Table, Row, Col, Statistic, Empty } from 'antd'
import {
  UserOutlined,
  CarOutlined,
  HomeOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  TagOutlined,
  TeamOutlined
} from '@ant-design/icons'

const { Option } = Select

const ClientesListView = ({ dadosClientes = [], loading = false }) => {
  // Largura dinâmica considerando a sidebar
  const [sidebarWidth, setSidebarWidth] = useState(0)

  useEffect(() => {
    const getSidebarEl = () => (
      document.querySelector('.ant-layout-sider') ||
      document.querySelector('.sidebar') ||
      document.querySelector('#sidebar') ||
      document.querySelector('[data-sidebar]')
    )

    const measure = () => {
      const el = getSidebarEl()
      const w = el ? el.getBoundingClientRect().width : 0
      setSidebarWidth(w)
    }

    // Medir ao montar e ao redimensionar janela
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])
  const [agrupamento, setAgrupamento] = useState('cliente')
  const [listData, setListData] = useState([])
  const [expandedRowKeys, setExpandedRowKeys] = useState([])


  // Função para agrupar dados em hierarquia Carro > Romaneio > Cliente
  const agruparDados = (dados, tipoAgrupamento) => {
    // Garantir que dados seja sempre um array
    const dadosArray = Array.isArray(dados) ? dados : []
    if (dadosArray.length === 0) return []

    // Inicializador comum de agregados
    const inicializarDados = () => ({
      notasUnicas: new Set(),
      notasImpressas: new Set(),
      notasNaoImpressas: new Set(),
      etiquetas: 0,
      etiquetasImp: 0,
      etiquetasSem: 0,
      peso: 0,
      volumes: 0,
      m3: 0,
      primeiraEtiqueta: null,
      ultimaEtiqueta: null,
      veiculos: new Set(),
      romaneios: new Set(),
      coletas: new Set(),
      statusColetaSet: new Set(),
      itemsRaw: [],
      cds: new Set(),
      rotas: new Set(),
    })

    const addItemToNode = (node, item) => {
      const chaveNota = `${item.Nota ?? item.NOTA ?? ''}_${item.Serie ?? item.SERIE ?? ''}`
      const peso = parseFloat(item.PESO) || 0
      const volumes = Number(item.VOLUMES ?? 0)
      const m3 = parseFloat(item.M3 ?? 0) || 0
      const etiquetaImpressa = item.STATUS_ETIQUETA === 1 ? 1 : 0
      const etiquetaSemImprimir = item.STATUS_ETIQUETA === 0 ? 1 : 0
      const dataEtiqueta = (item.STATUS_ETIQUETA === 1 && item.DATA_ETIQUETA) ? item.DATA_ETIQUETA : null

      node.dados.notasUnicas.add(chaveNota)
      node.dados.volumes += volumes
      node.dados.m3 += m3
      if (item.STATUS_ETIQUETA === 1) {
        node.dados.notasImpressas.add(chaveNota)
      } else {
        node.dados.notasNaoImpressas.add(chaveNota)
      }
      node.dados.etiquetas += 1
      node.dados.etiquetasImp += etiquetaImpressa
      node.dados.etiquetasSem += etiquetaSemImprimir
      node.dados.peso += peso
      node.dados.veiculos.add(item.CARRO || 'Sem Veículo')
      if (item.ROMANEIO !== undefined && item.ROMANEIO !== null) {
        node.dados.romaneios.add(item.ROMANEIO)
      }
      if (item.COLETA !== undefined && item.COLETA !== null) {
        node.dados.coletas.add(item.COLETA)
      }
      if (item.STATUS_COLETA !== undefined && item.STATUS_COLETA !== null) {
        node.dados.statusColetaSet.add(item.STATUS_COLETA)
      }
      node.dados.itemsRaw.push(item)

      if (item.CD !== undefined && item.CD !== null) {
        node.dados.cds.add(item.CD)
      }
      if (item.Rota !== undefined && item.Rota !== null) {
        node.dados.rotas.add(item.Rota)
      }

      if (dataEtiqueta) {
        if (!node.dados.primeiraEtiqueta || dataEtiqueta < node.dados.primeiraEtiqueta) {
          node.dados.primeiraEtiqueta = dataEtiqueta
        }
        if (!node.dados.ultimaEtiqueta || dataEtiqueta > node.dados.ultimaEtiqueta) {
          node.dados.ultimaEtiqueta = dataEtiqueta
        }
      }
    }

    const formatarHora = (dataVal) => {
      if (!dataVal) return '-'
      if (dataVal instanceof Date && !isNaN(dataVal.getTime())) {
        return dataVal.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
      }
      const s = String(dataVal)
      const m = s.match(/(?:T|\s)(\d{2}):(\d{2})/) || s.match(/(\d{2}):(\d{2})/)
      return m ? `${m[1]}:${m[2]}` : '-'
    }

    const finalizarNode = (node) => {
      node.dados.notas = node.dados.notasUnicas.size
      node.dados.notasImp = node.dados.notasImpressas.size
      node.dados.notasSem = node.dados.notasNaoImpressas.size
      node.dados.primeiraEtiquetaFormatada = formatarHora(node.dados.primeiraEtiqueta)
      node.dados.ultimaEtiquetaFormatada = formatarHora(node.dados.ultimaEtiqueta)

      node.dados.statusColeta = (node.dados.statusColetaSet.size === 1)
        ? Array.from(node.dados.statusColetaSet)[0]
        : (node.dados.statusColetaSet.size > 1 ? 'MISTO' : null)
      node.dados.romaneiosLista = Array.from(node.dados.romaneios)
      node.dados.coletasLista = Array.from(node.dados.coletas)
      node.dados.cdsLista = Array.from(node.dados.cds)
      node.dados.rotasLista = Array.from(node.dados.rotas)
      node.dados.cdValue = (node.dados.cds.size === 1) ? Array.from(node.dados.cds)[0] : (node.dados.cds.size > 1 ? 'MISTO' : '-')
      node.dados.rotaValue = (node.dados.rotas.size === 1) ? Array.from(node.dados.rotas)[0] : (node.dados.rotas.size > 1 ? 'MISTO' : '-')

      delete node.dados.notasUnicas
      delete node.dados.notasImpressas
      delete node.dados.notasNaoImpressas
      delete node.dados.veiculos
      delete node.dados.primeiraEtiqueta
      delete node.dados.ultimaEtiqueta
      delete node.dados.statusColetaSet
      delete node.dados.romaneios
      delete node.dados.coletas
      delete node.dados.cds
      delete node.dados.rotas
    }

    const toArrayRecursive = (childrenObj) => {
      return Object.values(childrenObj).map(child => {
        if (child.children && typeof child.children === 'object') {
          child.children = toArrayRecursive(child.children)
        } else {
          child.children = []
        }
        finalizarNode(child)
        return child
      })
    }

    if (tipoAgrupamento === 'veiculo') {
      // Carro > Romaneio > Cliente
      const carros = {}
      dadosArray.forEach(item => {
        const carroKey = item.CARRO || 'Sem Veículo'
        if (!carros[carroKey]) {
          carros[carroKey] = {
            key: `car:${carroKey}`,
            title: carroKey,
            icon: <CarOutlined />,
            nivel: 1,
            tipo: 'carro',
            children: {},
            dados: inicializarDados()
          }
        }
        addItemToNode(carros[carroKey], item)

        const romKey = (item.ROMANEIO !== undefined && item.ROMANEIO !== null) ? item.ROMANEIO : 'Sem Romaneio'
        if (!carros[carroKey].children[romKey]) {
          carros[carroKey].children[romKey] = {
            key: `${carros[carroKey].key}:rom:${romKey}`,
            title: `Romaneio ${romKey}`,
            icon: <FileTextOutlined />,
            nivel: 2,
            tipo: 'romaneio',
            children: {},
            dados: inicializarDados()
          }
        }
        addItemToNode(carros[carroKey].children[romKey], item)

        const cliKey = item.CLIENTE || 'Sem Cliente'
        if (!carros[carroKey].children[romKey].children[cliKey]) {
          carros[carroKey].children[romKey].children[cliKey] = {
            key: `${carros[carroKey].key}:rom:${romKey}:cli:${cliKey}`,
            title: cliKey,
            icon: <UserOutlined />,
            nivel: 3,
            tipo: 'cliente',
            children: {},
            dados: inicializarDados()
          }
        }
        const cliNode = carros[carroKey].children[romKey].children[cliKey]
        addItemToNode(cliNode, item)

        // Adicionar nível CD -> Praça abaixo de Cliente
        const cdKey = (item.CD !== undefined && item.CD !== null) ? item.CD : 'Sem CD'
        if (!cliNode.children[cdKey]) {
          cliNode.children[cdKey] = {
            key: `${cliNode.key}:cd:${cdKey}`,
            title: `CD ${cdKey}`,
            icon: <HomeOutlined />,
            nivel: 4,
            tipo: 'cd',
            children: {},
            dados: inicializarDados()
          }
        }
        const cdNode = cliNode.children[cdKey]
        addItemToNode(cdNode, item)

        const rotaKey = (item.Rota !== undefined && item.Rota !== null) ? item.Rota : 'Sem Rota'
        if (!cdNode.children[rotaKey]) {
          cdNode.children[rotaKey] = {
            key: `${cdNode.key}:rota:${rotaKey}`,
            title: `Rota ${rotaKey}`,
            icon: <EnvironmentOutlined />,
            nivel: 5,
            tipo: 'rota',
            children: {},
            dados: inicializarDados()
          }
        }
        addItemToNode(cdNode.children[rotaKey], item)
      })

      const raiz = Object.values(carros)
      raiz.forEach(c => {
        c.children = toArrayRecursive(c.children)
        finalizarNode(c)
      })
      return raiz
    }

    if (tipoAgrupamento === 'cd') {
      // CD > Rota
      const cds = {}
      dadosArray.forEach(item => {
        const cdKey = (item.CD !== undefined && item.CD !== null) ? item.CD : 'Sem CD'
        if (!cds[cdKey]) {
          cds[cdKey] = {
            key: `cd:${cdKey}`,
            title: `CD ${cdKey}`,
            icon: <HomeOutlined />,
            nivel: 1,
            tipo: 'cd',
            children: {},
            dados: inicializarDados()
          }
        }
        addItemToNode(cds[cdKey], item)

        const rotaKey = (item.Rota !== undefined && item.Rota !== null) ? item.Rota : 'Sem Rota'
        if (!cds[cdKey].children[rotaKey]) {
          cds[cdKey].children[rotaKey] = {
            key: `${cds[cdKey].key}:rota:${rotaKey}`,
            title: `Rota ${rotaKey}`,
            icon: <EnvironmentOutlined />,
            nivel: 2,
            tipo: 'rota',
            children: {},
            dados: inicializarDados()
          }
        }
        addItemToNode(cds[cdKey].children[rotaKey], item)
      })

      const raiz = Object.values(cds)
      raiz.forEach(c => {
        c.children = toArrayRecursive(c.children)
        finalizarNode(c)
      })
      return raiz
    }

    if (tipoAgrupamento === 'rota') {
      // Rota > CD
      const rotas = {}
      dadosArray.forEach(item => {
        const rotaKey = (item.Rota !== undefined && item.Rota !== null) ? item.Rota : 'Sem Rota'
        if (!rotas[rotaKey]) {
          rotas[rotaKey] = {
            key: `rota:${rotaKey}`,
            title: `Rota ${rotaKey}`,
            icon: <EnvironmentOutlined />,
            nivel: 1,
            tipo: 'rota',
            children: {},
            dados: inicializarDados()
          }
        }
        addItemToNode(rotas[rotaKey], item)

        const cdKey = (item.CD !== undefined && item.CD !== null) ? item.CD : 'Sem CD'
        if (!rotas[rotaKey].children[cdKey]) {
          rotas[rotaKey].children[cdKey] = {
            key: `${rotas[rotaKey].key}:cd:${cdKey}`,
            title: `CD ${cdKey}`,
            icon: <HomeOutlined />,
            nivel: 2,
            tipo: 'cd',
            children: {},
            dados: inicializarDados()
          }
        }
        addItemToNode(rotas[rotaKey].children[cdKey], item)
      })

      const raiz = Object.values(rotas)
      raiz.forEach(c => {
        c.children = toArrayRecursive(c.children)
        finalizarNode(c)
      })
      return raiz
    }

    // Fallback: Cliente > Carro > CD
    const clientes = {}
    dadosArray.forEach(item => {
      const clienteKey = item.CLIENTE || 'Sem Cliente'
      if (!clientes[clienteKey]) {
        clientes[clienteKey] = {
          key: `cli:${clienteKey}`,
          title: clienteKey,
          icon: <UserOutlined />,
          nivel: 1,
          tipo: 'cliente',
          children: {},
          dados: inicializarDados()
        }
      }
      const clienteNode = clientes[clienteKey]
      addItemToNode(clienteNode, item)

      const carroKey = item.CARRO || 'Sem Veículo'
      if (!clienteNode.children[carroKey]) {
        clienteNode.children[carroKey] = {
          key: `${clienteNode.key}:car:${carroKey}`,
          title: carroKey,
          icon: <CarOutlined />,
          nivel: 2,
          tipo: 'carro',
          children: {},
          dados: inicializarDados()
        }
      }
      const carroNode = clienteNode.children[carroKey]
      addItemToNode(carroNode, item)

      // Adicionar nível de CD sob CARRO para exibir dados ao expandir
      const cdKey = (item.CD !== undefined && item.CD !== null) ? item.CD : 'Sem CD'
      if (!carroNode.children[cdKey]) {
        carroNode.children[cdKey] = {
          key: `${carroNode.key}:cd:${cdKey}`,
          title: `CD ${cdKey}`,
          icon: <HomeOutlined />,
          nivel: 3,
          tipo: 'cd',
          children: {},
          dados: inicializarDados()
        }
      }
      const cdNode = carroNode.children[cdKey]
      addItemToNode(cdNode, item)

      // Adicionar nível Rota sob CD
      const rotaKey = (item.Rota !== undefined && item.Rota !== null) ? item.Rota : 'Sem Rota'
      if (!cdNode.children[rotaKey]) {
        cdNode.children[rotaKey] = {
          key: `${cdNode.key}:rota:${rotaKey}`,
          title: `Rota ${rotaKey}`,
          icon: <EnvironmentOutlined />,
          nivel: 4,
          tipo: 'rota',
          children: {},
          dados: inicializarDados()
        }
      }
      addItemToNode(cdNode.children[rotaKey], item)
    })
    const raiz = Object.values(clientes)
    raiz.forEach(c => {
      c.children = toArrayRecursive(c.children)
      finalizarNode(c)
    })
    return raiz
  }

  // Definir colunas da tabela
  const columns = [
    {
      title: 'Descrição',
      dataIndex: 'title',
      key: 'title',
      width: 260,
      ellipsis: true,
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>{record.icon}</span>
          <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={text}>{text}</span>
        </div>
      ),
    },

    {
      title: 'Notas',
      dataIndex: 'notas',
      key: 'notas',
      width: 80,
      align: 'center',
    },
    {
      title: 'Volumes',
      dataIndex: 'volumes',
      key: 'volumes',
      width: 90,
      align: 'center',
    },
    {
      title: 'Notas Imp',
      dataIndex: 'notasImp',
      key: 'notasImp',
      width: 100,
      align: 'center',
    },
    {
      title: 'Notas Sem',
      dataIndex: 'notasSem',
      key: 'notasSem',
      width: 100,
      align: 'center',
    },
    {
      title: 'Etiquetas Imp',
      dataIndex: 'etiquetasImp',
      key: 'etiquetasImp',
      width: 120,
      align: 'center',
    },
    {
      title: 'Etiquetas Sem',
      dataIndex: 'etiquetasSem',
      key: 'etiquetasSem',
      width: 120,
      align: 'center',
    },
    {
      title: 'Etiquetas',
      dataIndex: 'etiquetas',
      key: 'etiquetas',
      width: 100,
      align: 'center',
    },
    {
      title: 'Peso',
      dataIndex: 'peso',
      key: 'peso',
      width: 100,
      align: 'center',
      render: (value) => `${value?.toFixed(2) || 0}`,
    },
    {
      title: 'M3',
      dataIndex: 'm3',
      key: 'm3',
      width: 90,
      align: 'center',
      render: (value) => `${Number(value || 0).toFixed(2)}`,
    },
    {
      title: '1ª',
      dataIndex: 'primeiraEtiqueta',
      key: 'primeiraEtiqueta',
      width: 120,
      align: 'center',
    },
    {
      title: 'Última',
      dataIndex: 'ultimaEtiqueta',
      key: 'ultimaEtiqueta',
      width: 120,
      align: 'center',
    },
    // Removido Romaneio/Coleta agregados para focar na hierarquia
    {
      title: 'Status',
      dataIndex: 'statusColeta',
      key: 'statusColeta',
      width: 130,
      align: 'center',
    },
  ]

  // Calcular dados da lista usando useMemo
  const listDataCalculado = useMemo(() => {
    return agruparDados(dadosClientes, agrupamento)
  }, [dadosClientes, agrupamento])

  // Atualizar dados da lista evitando setState desnecessário
  useEffect(() => {
    setListData(prev => (prev === listDataCalculado ? prev : listDataCalculado))
  }, [listDataCalculado])

  // Função para expandir linhas
  const onExpand = (expanded, record) => {
    const newExpandedKeys = expanded
      ? [...expandedRowKeys, record.key]
      : expandedRowKeys.filter(key => key !== record.key)
    setExpandedRowKeys(newExpandedKeys)
  }

  // Função para renderizar linha expandida (subitens)
  const expandedRowRender = (record) => {
    if (record?.tipo !== 'rota') return null
    const detalhes = Array.isArray(record.itemsRaw) ? record.itemsRaw : []
    if (!detalhes || detalhes.length === 0) return null
    const colsDetalhe = [
      { title: 'Destinatário', dataIndex: 'destinatario', key: 'destinatario', width: 220 },
      { title: 'Nota', dataIndex: 'Nota', key: 'Nota', width: 90 },
      { title: 'Série', dataIndex: 'Serie', key: 'Serie', width: 80 },
      { title: 'Volumes', dataIndex: 'VOLUMES', key: 'VOLUMES', width: 90 },
      { title: 'Peso', dataIndex: 'PESO', key: 'PESO', width: 100, render: (v) => Number(v || 0).toFixed(2) },
      { title: 'M3', dataIndex: 'M3', key: 'M3', width: 90, render: (v) => Number(v || 0).toFixed(2) },
      { title: 'Pedido', dataIndex: 'pedido', key: 'pedido', width: 130 },
      { title: 'Cidade', dataIndex: 'cidade', key: 'cidade', width: 140 },
      { title: 'UF', dataIndex: 'uf', key: 'uf', width: 70 },
      { title: 'Setor', dataIndex: 'setor', key: 'setor', width: 90 }
    ]
    return (
      <Table
        columns={colsDetalhe}
        dataSource={detalhes.map((d, idx) => ({ ...d, key: `${record.key}_det_${idx}` }))}
        pagination={false}
        size="small"
        scroll={{ x: 'max-content' }}
      />
    )
  }

  // Preparar dados para a tabela
  const prepararDadosTabela = (dados) => {
    const dadosArray = Array.isArray(dados) ? dados : []
    if (dadosArray.length === 0) return []

    const mapNode = (item) => ({
      key: item.key,
      title: item.title,
      cd: item.dados.cdValue ?? '-',
      rota: item.dados.rotaValue ?? '-',
      notas: item.dados.notas,
      volumes: item.dados.volumes,
      notasImp: item.dados.notasImp,
      notasSem: item.dados.notasSem,
      etiquetasImp: item.dados.etiquetasImp,
      etiquetasSem: item.dados.etiquetasSem,
      etiquetas: item.dados.etiquetas,
      peso: item.dados.peso,
      m3: item.dados.m3,
      primeiraEtiqueta: item.dados.primeiraEtiquetaFormatada,
      ultimaEtiqueta: item.dados.ultimaEtiquetaFormatada,
      statusColeta: item.dados.statusColeta ?? '-',
      icon: item.icon,
      nivel: item.nivel,
      tipo: item.tipo,
      itemsRaw: item.dados.itemsRaw,
      children: Array.isArray(item.children) ? item.children.map(mapNode) : []
    })

    return dadosArray.map(mapNode)
  }

  // Calcular estatísticas totais
  const calcularEstatisticas = () => {
    // Garantir que dadosClientes seja sempre um array
    const dadosArray = Array.isArray(dadosClientes) ? dadosClientes : []
    if (dadosArray.length === 0) {
      return {
        totalClientes: 0,
        totalNotas: 0,
        totalEtiquetas: 0,
        totalPeso: 0
      }
    }

    const clientesUnicos = new Set(dadosArray.map(item => item.CLIENTE))
    const notasUnicas = new Set(dadosArray.map(item => `${item.Nota ?? item.NOTA}_${item.Serie ?? item.SERIE}`))
    const totalEtiquetas = dadosArray.length
    const totalPeso = dadosArray.reduce((sum, item) => sum + (parseFloat(item.PESO) || 0), 0)

    return {
      totalClientes: clientesUnicos.size,
      totalNotas: notasUnicas.size,
      totalEtiquetas,
      totalPeso
    }
  }

  const stats = calcularEstatisticas()
  // Garantir que listData seja sempre um array válido antes de processar
  const listDataArray = Array.isArray(listData) ? listData : []
  const dadosTabela = prepararDadosTabela(listDataArray)

  // Validação adicional para garantir que dadosTabela seja sempre um array válido
  const dadosTabelaValidados = Array.isArray(dadosTabela) ? dadosTabela : []

  return (
    <Card
      title="Clientes do Dia"
      variant="elevated"
      className="clientes-card"
      style={{
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}
      extra={
        <Select
          value={agrupamento}
          onChange={setAgrupamento}
          style={{ width: 120 }}
          size="small"
        >
          <Option value="cliente">Cliente</Option>
          <Option value="veiculo">Veículo</Option>
          <Option value="cd">CD</Option>
          <Option value="rota">Rota</Option>
        </Select>
      }
    >
      {/* Estatísticas resumidas */}
      <Row gutter={16} style={{ marginBottom: '16px' }}>
        <Col span={6}>
          <Statistic
            title="Clientes"
            value={stats.totalClientes}
            prefix={<TeamOutlined />}
            valueStyle={{ fontSize: '16px' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Notas"
            value={stats.totalNotas}
            prefix={<FileTextOutlined />}
            valueStyle={{ fontSize: '16px' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Etiquetas"
            value={stats.totalEtiquetas}
            prefix={<TagOutlined />}
            valueStyle={{ fontSize: '16px' }}
          />
        </Col>
        <Col span={6}>
          <Statistic
            title="Peso Total"
            value={stats.totalPeso}
            suffix="kg"
            precision={2}
            valueStyle={{ fontSize: '16px' }}
          />
        </Col>
      </Row>

      {/* ListView */}
      {Array.isArray(dadosTabelaValidados) && dadosTabelaValidados.length > 0 ? (
        <div style={{ width: '100%', maxWidth: sidebarWidth ? `calc(95vw - ${sidebarWidth}px)` : '100vw', overflowX: 'auto' }}>
          <Table
            columns={columns}
            dataSource={dadosTabelaValidados}
            loading={loading}
            pagination={false}
            size="small"
            tableLayout="fixed"
            expandable={{
              expandedRowRender,
              expandedRowKeys,
              onExpand,
              rowExpandable: (record) => (
                (Array.isArray(record.children) && record.children.length > 0) ||
                (record?.tipo === 'rota' && Array.isArray(record.itemsRaw) && record.itemsRaw.length > 0)
              ),
            }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 280px)' }}
            style={{
              background: 'transparent',
              fontSize: '12px'
            }}
          />
        </div>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Nenhum cliente encontrado para a data selecionada"
          style={{ padding: '40px 20px' }}
        />
      )}
    </Card>
  )
}

export default memo(ClientesListView)